'use client'

import { FC } from 'react'
import { Panel, PanelItem, PanelLayout } from '@/components/Panel'
import { Colour, SectionPaddingDefault } from '@/components/constants'
import cx from 'classnames'
import { PanelTitles } from '../PanelTitles'
import {
  CardProps,
  CmsCardPanelRow,
  CmsCardPanelSection,
  CmsQuoteCard,
  CompatibleCardItemType,
} from './CardPanelTypes'
import { useIsPrivacyPage } from '@/hooks/useIsPrivacyPage'
import styles from './styles.module.scss'
import { DynamicImage } from '../DynamicImage'
import { QuoteCard } from '@/components/QuoteCard/QuoteCard'
import { Card } from '@/components/Card/Card'

const CardTypeMap: Record<
  CompatibleCardItemType,
  FC<CmsQuoteCard | CardProps>
> = {
  [CompatibleCardItemType.ContentfulCardPanelCard]: Card,
  [CompatibleCardItemType.ContentfulQuoteCard]: QuoteCard,
}

export const CardPanel: FC<CmsCardPanelSection> = ({ fields, blog }) => {
  const {
    animateCardPosition,
    backgroundColour = 'teal20',
    backgroundImage,
    cardRows,
    ctaText,
    ctaUrl,
    downloadableAsset,
    downloadButtonText,
    footerText,
    increasedPadding,
    sectionPaddingBottom,
    sectionPaddingTop,
    showOpenAccountButton,
    subtitle,
    title,
  } = fields
  const multiCardRow = (row: CmsCardPanelRow): boolean =>
    row.fields?.cards?.length > 1
  const isPrivacy = useIsPrivacyPage()
  return (
    <Panel
      className={cx(styles.cardPanelSection)}
      layout={PanelLayout.full}
      colour={Colour[backgroundColour as Colour]}
      short
    >
      <PanelItem
        className={cx(styles.cardPanel, {
          [styles.blogItem]: blog,
          [styles.privacyItem]: isPrivacy,
        })}
      >
        <div
          className={cx(
            styles.content,
            styles[sectionPaddingTop || SectionPaddingDefault.TOP],
            styles[sectionPaddingBottom || SectionPaddingDefault.BOTTOM],
          )}
        >
          <div
            className={cx(styles.container, {
              [styles.increasedPadding]: increasedPadding,
            })}
          >
            <PanelTitles
              title={title}
              subtitle={subtitle}
              ctaText={ctaText}
              ctaUrl={ctaUrl}
              downloadableAsset={downloadableAsset}
              downloadButtonText={downloadButtonText}
              showOpenAccountButton={showOpenAccountButton}
            />
            {cardRows?.map((row: CmsCardPanelRow) => {
              return (
                <div
                  key={row.sys.id}
                  className={cx(styles.row, {
                    [styles.multiCardRow]: multiCardRow(row),
                  })}
                >
                  {row.fields?.cards.map((card, index) => {
                    const Component =
                      CardTypeMap[
                        card.sys.contentType.sys.id as keyof Record<
                          CompatibleCardItemType,
                          FC<CmsQuoteCard | CardProps>
                        >
                      ]

                    return Component ? (
                      <div key={card.sys.id} className={styles.cardContainer}>
                        <Component
                          animateCardPosition={animateCardPosition}
                          {...card}
                          cardPanel={true}
                          rowLength={row.fields.cards.length}
                          index={index}
                        />
                      </div>
                    ) : null
                  })}
                </div>
              )
            })}
            {footerText && <h5 className={styles.footerText}>{footerText}</h5>}
          </div>
        </div>
      </PanelItem>
      {backgroundImage && (
        <div className={styles.backgroundImage}>
          <DynamicImage
            className={styles.image}
            image={backgroundImage}
            itemProp="image"
            alt={''}
            fill
            style={{ objectFit: 'cover' }}
          />
        </div>
      )}
    </Panel>
  )
}
